import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type MetaFunction } from 'react-router'
import { serverOnly$ } from 'vite-env-only/macros'
import { getDomainUrl } from '#app/utils/misc.tsx'
import { PublicArticleLayout } from '../components/public-article-layout.tsx'
import { type Route } from './+types/porque-no-deberias-usar-claude-para-consultas-legales-en-costa-rica'

const slug = 'porque-no-deberias-usar-claude-para-consultas-legales-en-costa-rica'
const title = 'Por qué no deberías usar Claude para consultas legales en Costa Rica'
const description =
	'Claude es uno de los modelos de IA más capaces para razonar y redactar, pero una consulta legal costarricense necesita normativa local, fuentes comprobables y criterio jurídico. Por eso IUS PRO es la herramienta correcta.'
const publishedAt = '2026-05-30'

const articleBody = `
Claude, de Anthropic, es uno de los modelos de inteligencia artificial más capaces que existen hoy. Razona bien, mantiene conversaciones largas con coherencia, sigue instrucciones complejas y redacta con un nivel notable. Para analizar textos, ordenar argumentos o preparar un primer borrador, es una herramienta excelente.

Justamente por lo bien que escribe, conviene tener cuidado. Una consulta legal en Costa Rica no se resuelve con una respuesta que suene sólida; se resuelve con la norma correcta, vigente y aplicable al caso. Y eso es algo que un modelo general no garantiza, por más impecable que sea su redacción.

El planteamiento de fondo es simple: **una IA brillante para razonar no es lo mismo que una IA construida sobre el derecho costarricense**.

## Razonar bien no es lo mismo que conocer el derecho de Costa Rica

Claude destaca por su capacidad de razonamiento. Pero razonar a partir de información incompleta o desactualizada lleva a conclusiones que parecen correctas y no lo son. Un modelo general no tiene, de fábrica, acceso garantizado a la normativa costarricense vigente ni a la jurisprudencia nacional actualizada.

Cuando un abogado consulta un caso, necesita certezas concretas:

- la ley o el artículo exacto que respalda la respuesta;
- la confirmación de que esa norma no fue derogada ni reformada;
- la diferencia entre lo que dice la ley y lo que es interpretación;
- la advertencia de si el criterio proviene de otra jurisdicción;
- y la posibilidad de abrir la fuente y leerla por sí mismo.

Claude puede entregar un análisis ordenado y persuasivo, y aun así apoyarse en una norma que no aplica en Costa Rica o en un precedente que no existe. El razonamiento es bueno; el problema es la base sobre la que razona.

## Un análisis convincente puede esconder un error de fondo

En derecho, una respuesta elegante y mal fundamentada es más riesgosa que una equivocación evidente. Cuando Claude estructura un argumento con claridad, da confianza, y esa confianza es precisamente lo que hace difícil detectar el fallo.

Estos son los errores que un modelo general puede cometer sin avisar:

- construir un argumento sólido sobre una norma equivocada;
- citar artículos o sentencias que no existen;
- aplicar conceptos de derecho español, mexicano o estadounidense a un caso costarricense;
- pasar por alto una reforma reciente;
- ignorar plazos, competencia o requisitos procesales del caso real.

En la práctica profesional, esos detalles definen el resultado. Una cita inventada en un escrito o un plazo mal calculado no son tropiezos menores: comprometen el caso, la relación con el cliente y la responsabilidad del abogado.

## Sin fuentes comprobables, el ahorro de tiempo desaparece

Lo que distingue un buen criterio jurídico es que se puede sostener: se sabe de dónde viene, en qué norma se apoya y qué límites tiene. Una respuesta que no se puede verificar obliga a rehacer el trabajo.

Si el abogado tiene que revisar cada afirmación de Claude, confirmar que las citas son reales y comprobar que no se coló derecho de otro país, el tiempo que creía ganar se le va en validar. La velocidad sin trazabilidad no es una ventaja real.

Por eso una IA legal especializada en Costa Rica resuelve un problema distinto al de un asistente general.

## Qué hace diferente a IUS PRO

IUS PRO no compite por ser el modelo que mejor razona en abstracto. Está construido para el trabajo jurídico costarricense, con un objetivo concreto: que el abogado avance más rápido sin renunciar al control sobre el fundamento de cada respuesta.

Lo que cambia es el enfoque del producto:

- **Centrado en Costa Rica.** Normativa, jurisprudencia y práctica legal nacional como base del trabajo, no como un dato más entre muchos países.
- **Respuestas que se pueden comprobar.** El abogado revisa el fundamento; no depende de que una cita simplemente suene creíble.
- **Hecho para el oficio.** Más que un chat: investigación, análisis de expedientes, revisión de documentos y redacción de escritos con contexto jurídico.
- **Pensado en expedientes.** El valor está en entender hechos, documentos, estrategia y próximos pasos, no en contestar preguntas aisladas.
- **Redacción legal aplicada.** Convierte el análisis en documentos de trabajo: contratos, escritos, criterios y comunicaciones.
- **Confidencialidad profesional.** Diseñado para información sensible de clientes y equipos legales.

La idea no es sustituir el juicio del abogado, sino darle una herramienta que respeta cómo trabaja un profesional del derecho en Costa Rica.

## Dónde sí encaja Claude

Reconocer los límites de Claude para consultas legales no significa descartarlo. Para tareas de apoyo es muy útil:

- reformular un texto para que se entienda mejor;
- sintetizar documentación no confidencial;
- contrastar la estructura de un argumento;
- explorar enfoques antes de investigar a fondo;
- traducir o simplificar lenguaje técnico.

El límite aparece cuando la tarea exige derecho costarricense aplicable, citas verificables o estrategia procesal. Ahí, la calidad de la redacción no compensa la falta de una base jurídica confiable.

## La herramienta correcta depende de lo que está en juego

Cuanto mayor sea el impacto de una respuesta, más importa de dónde sale. Pedir ayuda para ordenar ideas es muy distinto de definir si una acción está prescrita, si una cláusula es nula o si conviene objetar una prueba.

Para esas decisiones, el abogado necesita una herramienta con enfoque local, normativa al día, manejo de expedientes y fuentes que pueda revisar antes de actuar. Eso es lo que ofrece IUS PRO, y por eso es la opción adecuada para usar IA de forma profesional en el ejercicio del derecho.

## Conclusión

Claude es un modelo extraordinario para razonar y escribir. Pero una consulta legal en Costa Rica no se mide por lo bien redactada que está la respuesta, sino por la solidez de su fundamento.

La pregunta no es si usar inteligencia artificial, sino con qué herramienta hacerlo según la tarea. Para pensar y redactar en general, Claude ayuda. Para investigar, analizar expedientes y producir trabajo jurídico costarricense verificable, la respuesta es IUS PRO.
`

export const handle: SEOHandle = {
	getSitemapEntries: serverOnly$(async () => [
		{
			route: `/articulos/${slug}`,
			priority: 0.8,
			changefreq: 'monthly',
			lastmod: `${publishedAt}T00:00:00.000Z`,
		},
	]),
}

export async function loader({ request }: Route.LoaderArgs) {
	return {
		canonicalUrl: `${getDomainUrl(request)}/articulos/${slug}`,
	}
}

export const meta: MetaFunction<typeof loader> = ({ loaderData }) => {
	const canonicalUrl = loaderData?.canonicalUrl

	return [
		{ title: `${title} | IUS PRO` },
		{ name: 'description', content: description },
		{
			name: 'keywords',
			content:
				'Claude consultas legales Costa Rica, IA legal Costa Rica, IUS PRO, Anthropic abogados, inteligencia artificial jurídica, jurisprudencia Costa Rica, normativa Costa Rica',
		},
		{ name: 'og:title', content: title },
		{ name: 'og:description', content: description },
		{ name: 'og:type', content: 'article' },
		...(canonicalUrl
			? [
					{ name: 'og:url', content: canonicalUrl },
					{ tagName: 'link', rel: 'canonical', href: canonicalUrl },
				]
			: []),
	]
}

export default function ClaudeLegalConsultasCostaRicaArticle() {
	return (
		<PublicArticleLayout
			title={title}
			description={description}
			body={articleBody}
			publishedAt={publishedAt}
			reviewDate={publishedAt}
		/>
	)
}
